import React, { memo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import { useStyles } from './styles';
import { GeneralModel, UserModel } from 'modules/models';
import Loader from 'modules/views/shared/Loader';
import { Grid } from '@material-ui/core';

export interface IApproveUserProps {
  approveUserResult: any;
  approveUserLoading: GeneralModel.ILoadingStatus;
  approveUser: (userId: string, companyId: string, projectId: string) => void;
  companyUserProfile: any;
  fetchUserLoading: GeneralModel.ILoadingStatus;
  updateUserLoading: GeneralModel.ILoadingStatus;
  fetchUserProfile: (companyId: string, companyUserId: string) => void;
  updateUserProfile: (companyId: string, companyUserId: string, user: UserModel.IAccount, isCurrentUserCompanyRemoved: boolean) => void;
  groupList: any;
  fetchGroupSearch: (searchRequest: any) => void;
}

export interface IQueryParams {
  companyUserId: string;
  userId: string;
  companyId: string;
  projectId: string;
}

const ApproveUser = ({
  approveUserResult,
  approveUserLoading,
  approveUser,
  companyUserProfile,
  fetchUserLoading,
  updateUserLoading,
  fetchUserProfile,
  updateUserProfile,
  groupList,
  fetchGroupSearch,
}: IApproveUserProps) => {
  const location = useLocation();
  const classes = useStyles();

  const { companyUserId, userId, companyId, projectId } = useParams<IQueryParams>();

  useEffect(() => {
    if (!companyId) return;
    let searchRequest = {
      paging: {
        clientSidePagination: true,
      },
      expand: ['company', 'metadata', 'parentGroup', 'labels'],
      searchParameters: [
        {
          field: 'companyId',
          operator: 'equals',
          value: companyId,
        },
        {
          field: 'type',
          operator: 'notequals',
          value: 'Client_Project',
        },
        {
          field: 'type',
          operator: 'notequals',
          value: 'FCA_Project',
        },
        {
          condition: 'or',
          searchParameters: [
            {
              field: 'hasAppAccessWebPermission',
              operator: 'eq',
              value: 'true',
            },
            {
              field: 'hasAppAccessJobSitePermission',
              operator: 'eq',
              value: 'true',
            },
            {
              field: 'hasAppAccessWorkerPermission',
              operator: 'eq',
              value: 'true',
            },
            {
              field: 'hasAppAccessTabletExperiencePermission',
              operator: 'eq',
              value: 'true',
            },
          ],
        },
        {
          condition: 'or',
          searchParameters: [
            {
              field: 'hasAdminLabel',
              operator: 'eq',
              value: 'true',
            },
            {
              field: 'hasUserLabel',
              operator: 'eq',
              value: 'true',
            },
            {
              field: 'hasOSRLabel',
              operator: 'eq',
              value: 'true',
            },
          ],
        },
      ],
    };

    fetchGroupSearch(searchRequest);
  }, [companyId, fetchGroupSearch]);

  useEffect(() => {
    if (userId && companyId && projectId) {
      if (localStorage.getItem('approve' + userId + '-' + companyId + '-' + projectId) === 'true') {
        localStorage.removeItem('approve' + userId + '-' + companyId + '-' + projectId);
        approveUser(userId, companyId, projectId);
      } else {
        localStorage.setItem('approve' + userId + '-' + companyId + '-' + projectId, 'true');
      }
    }
  }, []);

  useEffect(() => {
    if (approveUserResult) {
      // fetchUserProfile(companyId, userId);
      fetchUserProfile(companyId, companyUserId);
    }
  }, [approveUserResult]);

  useEffect(() => {
    if (companyUserProfile && companyUserProfile.id && groupList && groupList.length) {
      let filteredList = groupList
        .map(group => {
          return {
            ...group,
            label: group.name,
            value: group.id,
          };
        })
        .filter(group => !group.label.toLowerCase().includes('worker'));
      filteredList = filteredList.filter(
        (uig: any) =>
          uig.value === 0 ||
          !uig.isSystem ||
          (uig.code && ((uig.code.toLowerCase() === 'admin' && uig.hasAdminLabel) || (uig.code.toLowerCase() === 'user' && uig.hasUserLabel)))
      );
      const adminGroup = filteredList.find(fl => fl.label.toLowerCase() === 'admin');
      updateUserProfile(
        companyId,
        // userId,
        companyUserId,
        {
          ...companyUserProfile,
          oldGroupIds: [],
          newGroupIds: [],
          isWorker: false,
          resendInvitation: true,
          OldImpersonateToCompaniesIds: [],
          NewImpersonateToCompaniesIds: [],
          IsForImpersonateAccessOnly: false,
          VisibleCompanyAccessIds: [companyId],
          oldcompanyUserAccessData: companyUserProfile.companyUserAccessData,
          companyUserAccessData: {
            companyId: companyId,
            companyName: '',
            companyGroupProjects: [
              { isAdmin: true, isUser: false, groupId: adminGroup && adminGroup.value ? adminGroup.value : '', groupName: '', projects: [] },
            ],
          },
          oldCompanyUserImpersonateAccessList: companyUserProfile.companyUserImpersonateAccessList,
          companyUserImpersonateAccessList: [],
        } as any,
        false
      );
    }
  }, [companyUserProfile, groupList]);

  return (
    <Grid className={classes.container}>
      {approveUserLoading && approveUserLoading.isLoading ? (
        <Grid>Loading...</Grid>
      ) : (
        <Grid>
          {approveUserResult && <Grid className={classes.successMessage}>User approved successfully.</Grid>}
          {approveUserLoading && approveUserLoading.error && approveUserLoading.error.length > 0 && (
            <Grid className={classes.errorMessage}>{approveUserLoading.error}</Grid>
          )}
        </Grid>
      )}
      {((approveUserLoading && approveUserLoading.isLoading) ||
        (fetchUserLoading && fetchUserLoading.isLoading) ||
        (updateUserLoading && updateUserLoading.isLoading)) && <Loader />}
    </Grid>
  );
};

export default memo(ApproveUser);
